import React, { useState } from 'react';
import { Brain, RefreshCw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

export type CharacterName = 'Malcolm' | 'Dewey' | 'Reese' | 'Francis' | 'Lois' | 'Hal';

type Language = 'en' | 'zh' | 'ru' | 'fr' | 'de' | 'nl' | 'ms';

interface Question {
  question: string;
  options: Record<Language, string[]>;
  characters: CharacterName[];
}

interface CharacterScore {
  name: CharacterName;
  score: number;
}

interface Props {
  questions: Question[];
}

type CharacterTraits = Record<Language, Record<CharacterName, string[]>>;

interface CharacterDetail {
  description: Record<Language, string>;
  recommendedEpisodes: Array<{
    season: number;
    episode: number;
    title: Record<Language, string>;
    description: Record<Language, string>;
  }>;
  similarCharacters: CharacterName[];
}

const characterDetails: Record<CharacterName, CharacterDetail> = {
  Malcolm: {
    description: {
      en: "A child prodigy with an IQ of 165, Malcolm often struggles to balance his intellectual gifts with normal teenage life. His analytical mind and direct honesty sometimes create social challenges, but his strong moral compass and family loyalty always shine through.",
      zh: "天才少年，智商165，经常在天赋与正常青少年生活之间寻找平衡。他的分析思维和直率诚实有时会带来社交挑战，但他强烈的道德感和家庭观念始终闪耀其中。",
      ru: "Вундеркинд с IQ 165, Малкольм часто пытается найти баланс между своими интеллектуальными способностями и нормальной подростковой жизнью. Его аналитический ум и прямота иногда создают социальные проблемы, но его сильный моральный компас и преданность семье всегда проявляются.",
      fr: "Un enfant prodige avec un QI de 165, Malcolm lutte souvent pour équilibrer ses dons intellectuels avec une vie d'adolescent normale. Son esprit analytique et son honnêteté directe créent parfois des défis sociaux, mais sa forte boussole morale et sa loyauté familiale brillent toujours.",
      de: "Ein Wunderkind mit einem IQ von 165, Malcolm kämpft oft damit, seine intellektuellen Gaben mit einem normalen Teenagerleben in Einklang zu bringen. Sein analytischer Verstand und seine direkte Ehrlichkeit schaffen manchmal soziale Herausforderungen, aber sein starker moralischer Kompass und seine Familienloyalität scheinen immer durch.",
      nl: "Een wonderkind met een IQ van 165, Malcolm worstelt vaak om zijn intellectuele gaven te balanceren met een normaal tienerleven. Zijn analytische geest en directe eerlijkheid creëren soms sociale uitdagingen, maar zijn sterke morele kompas en familieloyaliteit schijnen altijd door.",
      ms: "Seorang anak genius dengan IQ 165, Malcolm sering bergelut untuk mengimbangi bakat intelektualnya dengan kehidupan remaja normal. Pemikiran analitisnya dan kejujuran langsungnya kadang-kadang menciptakan cabaran sosial, tetapi kompas moral dan kesetiaan keluarganya selalu bersinar."
    },
    recommendedEpisodes: [
      {
        season: 1,
        episode: 1,
        title: {
          en: "Pilot",
          zh: "试播集",
          ru: "Пилот",
          fr: "Pilote",
          de: "Pilot",
          nl: "Pilot",
          ms: "Pilot"
        },
        description: {
          en: "Malcolm is diagnosed as a genius and transferred to the gifted class.",
          zh: "Malcolm被诊断为天才并转入天才班。",
          ru: "Малкольма диагностируют как гения и переводят в класс для одаренных.",
          fr: "Malcolm est diagnostiqué comme un génie et transféré dans la classe des surdoués.",
          de: "Malcolm wird als Genie diagnostiziert und in die Begabtenklasse versetzt.",
          nl: "Malcolm wordt gediagnosticeerd als een genie en overgeplaatst naar de begaafdenklas.",
          ms: "Malcolm didiagnosis sebagai genius dan dipindahkan ke kelas berbakat."
        }
      }
    ],
    similarCharacters: ['Dewey', 'Lois']
  },
  Dewey: {
    description: {
      en: "The often overlooked musical prodigy of the family, Dewey possesses both creative genius and emotional intelligence. His unique perspective and adaptability help him navigate family chaos while developing his artistic talents.",
      zh: "家中常被忽视的音乐天才，Dewey既有创造性天赋又有情商。他独特的视角和适应能力帮助他在家庭混乱中生存，同时发展他的艺术才能。",
      ru: "Часто незамеченный музыкальный вундеркинд семьи, Дьюи обладает как творческим гением, так и эмоциональным интеллектом. Его уникальная перспектива и адаптивность помогают ему ориентироваться в семейном хаосе, развивая свои художественные таланты.",
      fr: "Le prodige musical souvent négligé de la famille, Dewey possède à la fois un génie créatif et une intelligence émotionnelle. Sa perspective unique et son adaptabilité l'aident à naviguer dans le chaos familial tout en développant ses talents artistiques.",
      de: "Das oft übersehene musikalische Wunderkind der Familie, Dewey besitzt sowohl kreatives Genie als auch emotionale Intelligenz. Seine einzigartige Perspektive und Anpassungsfähigkeit helfen ihm, sich im Familienchaos zurechtzufinden und dabei seine künstlerischen Talente zu entwickeln.",
      nl: "Het vaak over het hoofd geziene muzikale wonderkind van de familie, Dewey bezit zowel creatief genie als emotionele intelligentie. Zijn unieke perspectief en aanpassingsvermogen helpen hem door de familiechaos te navigeren terwijl hij zijn artistieke talenten ontwikkelt.",
      ms: "Genius muzik keluarga yang sering diabaikan, Dewey memiliki kedua-dua genius kreatif dan kecerdasan emosi. Perspektif unik dan kebolehsuaiannya membantunya mengemudi kekacauan keluarga sambil mengembangkan bakat artistiknya."
    },
    recommendedEpisodes: [
      {
        season: 4,
        episode: 15,
        title: {
          en: "Dewey's Opera",
          zh: "Dewey的歌剧",
          ru: "Опера Дьюи",
          fr: "L'Opéra de Dewey",
          de: "Deweys Oper",
          nl: "Dewey's Opera",
          ms: "Opera Dewey"
        },
        description: {
          en: "Dewey composes a musical masterpiece about his family's daily life.",
          zh: "Dewey创作了一部关于家庭日常生活的音乐杰作。",
          ru: "Дьюи сочиняет музыкальный шедевр о повседневной жизни своей семьи.",
          fr: "Dewey compose un chef-d'œuvre musical sur la vie quotidienne de sa famille.",
          de: "Dewey komponiert ein musikalisches Meisterwerk über den Alltag seiner Familie.",
          nl: "Dewey componeert een muzikaal meesterwerk over het dagelijks leven van zijn familie.",
          ms: "Dewey mengarang karya agung muzik tentang kehidupan harian keluarganya."
        }
      }
    ],
    similarCharacters: ['Malcolm', 'Hal']
  },
  Reese: {
    description: {
      en: "While academically challenged, Reese is a culinary savant with a talent for creative cooking. His impulsive nature often leads to trouble, but his loyalty to family and straightforward approach to life make him uniquely endearing.",
      zh: "虽然学习成绩不佳，但Reese是一个烹饪天才，有创意料理的天赋。他冲动的性格经常惹麻烦，但他对家人的忠诚和对生活直接的态度使他特别讨人喜欢。",
      ru: "Хотя у него проблемы с учебой, Рис - кулинарный савант с талантом к творческой кухне. Его импульсивная натура часто приводит к проблемам, но его преданность семье и прямолинейный подход к жизни делают его особенно привлекательным.",
      fr: "Bien qu'ayant des difficultés académiques, Reese est un savant culinaire avec un talent pour la cuisine créative. Sa nature impulsive le mène souvent aux problèmes, mais sa loyauté envers la famille et son approche directe de la vie le rendent particulièrement attachant.",
      de: "Während er akademisch herausgefordert ist, ist Reese ein kulinarischer Savant mit einem Talent für kreatives Kochen. Seine impulsive Natur führt oft zu Problemen, aber seine Loyalität zur Familie und sein direkter Ansatz zum Leben machen ihn einzigartig liebenswert.",
      nl: "Hoewel academisch uitgedaagd, is Reese een culinair savant met een talent voor creatief koken. Zijn impulsieve natuur leidt vaak tot problemen, maar zijn loyaliteit aan familie en rechtdoorzee benadering van het leven maken hem uniek innemend.",
      ms: "Walaupun mencabar secara akademik, Reese adalah seorang savant kulinari dengan bakat untuk memasak kreatif. Sifat impulsifnya sering membawa kepada masalah, tetapi kesetiaannya kepada keluarga dan pendekatan terus-terang terhadap kehidupan membuatnya unik dan menarik."
    },
    recommendedEpisodes: [
      {
        season: 2,
        episode: 14,
        title: {
          en: "Dinner Out",
          zh: "外出晚餐",
          ru: "Ужин вне дома",
          fr: "Dîner dehors",
          de: "Essen gehen",
          nl: "Uit eten",
          ms: "Makan Malam Di Luar"
        },
        description: {
          en: "Reese's culinary talents are discovered when he helps prepare a gourmet meal.",
          zh: "当Reese帮助准备美食时，他的烹饪天赋被发现了。",
          ru: "Кулинарные таланты Риса обнаруживаются, когда он помогает готовить изысканную еду.",
          fr: "Les talents culinaires de Reese sont découverts lorsqu'il aide à préparer un repas gastronomique.",
          de: "Reeses kulinarische Talente werden entdeckt, als er hilft, eine Gourmetmahlzeit zuzubereiten.",
          nl: "Reese's culinaire talenten worden ontdekt wanneer hij helpt een gastronomische maaltijd te bereiden.",
          ms: "Bakat kulinari Reese ditemui apabila dia membantu menyediakan hidangan gourmet."
        }
      }
    ],
    similarCharacters: ['Francis', 'Hal']
  },
  Francis: {
    description: {
      en: "The rebellious eldest son who evolves from a troubled teen to a responsible adult. His journey of self-discovery and independence, while maintaining strong family bonds, showcases his complex character development.",
      zh: "叛逆的长子，从问题少年成长为负责任的成年人。他的自我发现和独立之旅，同时保持着强烈的家庭纽带，展示了他复杂的性格发展。",
      ru: "Мятежный старший сын, который эволюционирует от трудного подростка до ответственного взрослого. Его путь самопознания и независимости, при сохранении крепких семейных уз, показывает сложное развитие его характера.",
      fr: "Le fils aîné rebelle qui évolue d'un adolescent troublé à un adulte responsable. Son voyage de découverte de soi et d'indépendance, tout en maintenant des liens familiaux forts, montre le développement complexe de son caractère.",
      de: "Der rebellische älteste Sohn, der sich vom schwierigen Teenager zum verantwortungsvollen Erwachsenen entwickelt. Seine Reise der Selbstfindung und Unabhängigkeit, während er starke Familienbande aufrechterhält, zeigt seine komplexe Charakterentwicklung.",
      nl: "De rebelse oudste zoon die evolueert van een probleemtiener naar een verantwoordelijke volwassene. Zijn reis van zelfontdekking en onafhankelijkheid, terwijl hij sterke familiebanden onderhoudt, toont zijn complexe karakterontwikkeling.",
      ms: "Anak sulung yang memberontak yang berkembang dari remaja bermasalah kepada orang dewasa yang bertanggungjawab. Perjalanan penemuan diri dan kemerdekaan, sambil mengekalkan ikatan keluarga yang kuat, menunjukkan perkembangan watak yang kompleks."
    },
    recommendedEpisodes: [
      {
        season: 3,
        episode: 12,
        title: {
          en: "Hal's Birthday",
          zh: "Hal的生日",
          ru: "День рождения Хэла",
          fr: "L'anniversaire de Hal",
          de: "Hals Geburtstag",
          nl: "Hal's verjaardag",
          ms: "Hari Jadi Hal"
        },
        description: {
          en: "Francis returns home and shows signs of maturity while helping organize Hal's birthday celebration.",
          zh: "Francis回家后在帮助组织Hal的生日庆祝活动时表现出成熟的迹象。",
          ru: "Фрэнсис возвращается домой и проявляет признаки зрелости, помогая организовать празднование дня рождения Хэла.",
          fr: "Francis rentre à la maison et montre des signes de maturité en aidant à organiser la célébration d'anniversaire de Hal.",
          de: "Francis kehrt nach Hause zurück und zeigt Anzeichen von Reife, während er hilft, Hals Geburtstagsfeier zu organisieren.",
          nl: "Francis keert terug naar huis en toont tekenen van volwassenheid terwijl hij helpt Hal's verjaardagsviering te organiseren.",
          ms: "Francis pulang ke rumah dan menunjukkan tanda-tanda kematangan sambil membantu mengatur sambutan hari jadi Hal."
        }
      }
    ],
    similarCharacters: ['Reese', 'Malcolm']
  },
  Lois: {
    description: {
      en: "A strong-willed mother who maintains order through strict discipline while harboring deep love for her family. Her seemingly harsh methods often mask her determination to raise her children to be responsible and successful adults.",
      zh: "一位意志坚强的母亲，通过严格的纪律维持秩序，同时对家人怀有深深的爱。她表面严厉的方法常常掩盖着她要把孩子们培养成负责任和成功的成年人的决心。",
      ru: "Сильная духом мать, которая поддерживает порядок через строгую дисциплину, питая глубокую любовь к своей семье. Её кажущиеся жесткими методы часто маскируют её решимость воспитать детей ответственными и успешными взрослыми.",
      fr: "Une mère forte qui maintient l'ordre par une discipline stricte tout en nourrissant un profond amour pour sa famille. Ses méthodes apparemment dures masquent souvent sa détermination à élever ses enfants pour qu'ils deviennent des adultes responsables et réussis.",
      de: "Eine willensstarke Mutter, die durch strenge Disziplin Ordnung hält, während sie tiefe Liebe für ihre Familie hegt. Ihre scheinbar harten Methoden verbergen oft ihre Entschlossenheit, ihre Kinder zu verantwortungsvollen und erfolgreichen Erwachsenen zu erziehen.",
      nl: "Een sterke moeder die orde handhaaft door strikte discipline terwijl ze diepe liefde koestert voor haar familie. Haar ogenschijnlijk harde methoden maskeren vaak haar vastberadenheid om haar kinderen op te voeden tot verantwoordelijke en succesvolle volwassenen.",
      ms: "Seorang ibu yang berkemauan kuat yang mengekalkan ketertiban melalui disiplin yang ketat sambil menyimpan kasih sayang yang mendalam untuk keluarganya. Kaedahnya yang kelihatan keras sering menyembunyikan tekadnya untuk membesarkan anak-anaknya menjadi orang dewasa yang bertanggungjawab dan berjaya."
    },
    recommendedEpisodes: [
      {
        season: 2,
        episode: 25,
        title: {
          en: "Flashback",
          zh: "闪回",
          ru: "Воспоминания",
          fr: "Flashback",
          de: "Rückblende",
          nl: "Flashback",
          ms: "Kilas Balik"
        },
        description: {
          en: "A look back at how Lois and Hal met and fell in love, revealing their deep connection.",
          zh: "回顾Lois和Hal相遇并相爱的过程，揭示了他们深厚的感情。",
          ru: "Взгляд назад на то, как Лоис и Хэл встретились и влюбились, раскрывающий их глубокую связь.",
          fr: "Un regard en arrière sur la façon dont Lois et Hal se sont rencontrés et sont tombés amoureux, révélant leur connexion profonde.",
          de: "Ein Rückblick darauf, wie Lois und Hal sich trafen und verliebten, der ihre tiefe Verbindung offenbart.",
          nl: "Een terugblik op hoe Lois en Hal elkaar ontmoetten en verliefd werden, wat hun diepe verbinding onthult.",
          ms: "Pandangan ke belakang tentang bagaimana Lois dan Hal bertemu dan jatuh cinta, mendedahkan hubungan mendalam mereka."
        }
      }
    ],
    similarCharacters: ['Malcolm', 'Francis']
  },
  Hal: {
    description: {
      en: "A loving father whose childlike enthusiasm and creative passions contrast with his role as a family patriarch. His unconventional parenting style and dedication to his wife and children create many of the show's most heartwarming moments.",
      zh: "一位慈爱的父亲，他孩子般的热情和创造力与作为一家之主的角色形成对比。他非传统的育儿方式和对妻子儿女的奉献创造了剧中许多温馨的时刻。",
      ru: "Любящий отец, чей детский энтузиазм и творческие увлечения контрастируют с его ролью главы семьи. Его нетрадиционный стиль воспитания и преданность жене и детям создают многие из самых трогательных моментов шоу.",
      fr: "Un père aimant dont l'enthousiasme enfantin et les passions créatives contrastent avec son rôle de patriarche familial. Son style parental non conventionnel et son dévouement envers sa femme et ses enfants créent plusieurs des moments les plus touchants de la série.",
      de: "Ein liebevoller Vater, dessen kindliche Begeisterung und kreative Leidenschaften im Kontrast zu seiner Rolle als Familienpatriarch stehen. Sein unkonventioneller Erziehungsstil und seine Hingabe an seine Frau und Kinder schaffen viele der herzerwärmendsten Momente der Show.",
      nl: "Een liefdevolle vader wiens kinderlijk enthousiasme en creatieve passies contrasteren met zijn rol als familiehoofd. Zijn onconventionele opvoedingsstijl en toewijding aan zijn vrouw en kinderen creëren vele van de meest hartverwarmende momenten van de show.",
      ms: "Seorang ayah yang penyayang yang semangat kanak-kanak dan minat kreatifnya berbeza dengan peranannya sebagai ketua keluarga. Gaya keibubapaan yang tidak konvensional dan dedikasi kepada isteri dan anak-anaknya mencipta banyak momen paling mengharukan dalam rancangan ini."
    },
    recommendedEpisodes: [
      {
        season: 6,
        episode: 2,
        title: {
          en: "Hal's Dentist",
          zh: "Hal的牙医",
          ru: "Дантист Хэла",
          fr: "Le dentiste de Hal",
          de: "Hals Zahnarzt",
          nl: "Hal's tandarts",
          ms: "Doktor Gigi Hal"
        },
        description: {
          en: "Hal becomes obsessed with a new hobby while avoiding a dental appointment, showcasing his quirky personality.",
          zh: "Hal在逃避牙科预约时沉迷于一个新爱好，展现了他古怪的性格。",
          ru: "Хэл становится одержим новым хобби, избегая посещения стоматолога, что показывает его причудливую личность.",
          fr: "Hal devient obsédé par un nouveau passe-temps tout en évitant un rendez-vous chez le dentiste, montrant sa personnalité excentrique.",
          de: "Hal wird von einem neuen Hobby besessen, während er einen Zahnarzttermin vermeidet, was seine schrullige Persönlichkeit zeigt.",
          nl: "Hal raakt geobsedeerd door een nieuwe hobby terwijl hij een tandartsafspraak vermijdt, wat zijn eigenaardige persoonlijkheid laat zien.",
          ms: "Hal menjadi tergila-gila dengan hobi baru sambil mengelak temu janji gigi, menunjukkan personaliti peliknya."
        }
      }
    ],
    similarCharacters: ['Dewey', 'Reese']
  }
};

export const Quiz: React.FC<Props> = ({ questions }) => {
  const { t, language } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [characterScores, setCharacterScores] = useState<CharacterScore[]>([
    { name: 'Malcolm', score: 0 },
    { name: 'Dewey', score: 0 },
    { name: 'Reese', score: 0 },
    { name: 'Francis', score: 0 },
    { name: 'Hal', score: 0 },
    { name: 'Lois', score: 0 },
  ]);
  const [showResult, setShowResult] = useState(false);

  const characterTraits: CharacterTraits = {
    en: {
      Malcolm: ['Genius IQ', 'Honest & Direct', 'Socially Awkward', 'Responsible'],
      Dewey: ['Musical Prodigy', 'Imaginative', 'Adaptable', 'Independent Thinker'],
      Reese: ['Culinary Genius', 'Impulsive', 'Loyal', 'Straightforward'],
      Francis: ['Rebellious', 'Independent', 'Freedom-loving', 'Family-oriented'],
      Lois: ['Strong-willed', 'Fair & Just', 'Strict', 'Loving'],
      Hal: ['Empathetic', 'Family-loving', 'Easy-going', 'Creative'],
    },
    zh: {
      Malcolm: ['天才智商', '直率诚实', '社交困扰', '责任感强'],
      Dewey: ['音乐天才', '富有想象力', '适应能力强', '独立思考'],
      Reese: ['烹饪天才', '冲动好斗', '重情重义', '简单直接'],
      Francis: ['叛逆不羁', '独立自主', '重视自由', '家庭情结'],
      Lois: ['意志坚强', '公平正直', '严格管教', '充满爱心'],
      Hal: ['富有同理心', '热爱家庭', '随性而为', '创意十足'],
    },
    ru: {
      Malcolm: ['Гениальный IQ', 'Честный и прямой', 'Социально неловкий', 'Ответственный'],
      Dewey: ['Музыкальный вундеркинд', 'С богатым воображением', 'Адаптивный', 'Независимый мыслитель'],
      Reese: ['Кулинарный гений', 'Импульсивный', 'Верный', 'Прямолинейный'],
      Francis: ['Бунтарь', 'Независимый', 'Свободолюбивый', 'Семейный'],
      Lois: ['Волевая', 'Справедливая', 'Строгая', 'Любящая'],
      Hal: ['Эмпатичный', 'Семейный', 'Беззаботный', 'Креативный'],
    },
    fr: {
      Malcolm: ['QI de génie', 'Honnête et direct', 'Socialement maladroit', 'Responsable'],
      Dewey: ['Prodige musical', 'Imaginatif', 'Adaptable', 'Penseur indépendant'],
      Reese: ['Génie culinaire', 'Impulsif', 'Loyal', 'Direct'],
      Francis: ['Rebelle', 'Indépendant', 'Épris de liberté', 'Attaché à la famille'],
      Lois: ['Forte personnalité', 'Juste', 'Stricte', 'Aimante'],
      Hal: ['Empathique', 'Familial', 'Décontracté', 'Créatif'],
    },
    de: {
      Malcolm: ['Genie-IQ', 'Ehrlich & Direkt', 'Sozial unbeholfen', 'Verantwortungsvoll'],
      Dewey: ['Musikalisches Wunderkind', 'Fantasievoll', 'Anpassungsfähig', 'Unabhängiger Denker'],
      Reese: ['Kochgenie', 'Impulsiv', 'Loyal', 'Geradlinig'],
      Francis: ['Rebellisch', 'Unabhängig', 'Freiheitsliebend', 'Familienorientiert'],
      Lois: ['Willensstark', 'Fair & Gerecht', 'Streng', 'Liebevoll'],
      Hal: ['Einfühlsam', 'Familienliebend', 'Locker', 'Kreativ'],
    },
    nl: {
      Malcolm: ['Geniaal IQ', 'Eerlijk & Direct', 'Sociaal onhandig', 'Verantwoordelijk'],
      Dewey: ['Muzikaal wonderkind', 'Fantasierijk', 'Aanpasbaar', 'Onafhankelijke denker'],
      Reese: ['Culinair genie', 'Impulsief', 'Loyaal', 'Rechtdoorzee'],
      Francis: ['Rebels', 'Onafhankelijk', 'Vrijheidslievend', 'Familiemens'],
      Lois: ['Wilskrachtig', 'Eerlijk & Rechtvaardig', 'Streng', 'Liefdevol'],
      Hal: ['Empathisch', 'Familiemens', 'Makkelijk', 'Creatief'],
    },
    ms: {
      Malcolm: ['IQ Genius', 'Jujur & Terus-terang', 'Janggal secara sosial', 'Bertanggungjawab'],
      Dewey: ['Prodigy Muzik', 'Imaginatif', 'Mudah suai', 'Pemikir bebas'],
      Reese: ['Genius Masakan', 'Impulsif', 'Setia', 'Terus-terang'],
      Francis: ['Memberontak', 'Bebas', 'Pencinta kebebasan', 'Berorientasikan keluarga'],
      Lois: ['Berkemauan kuat', 'Adil & Saksama', 'Tegas', 'Penyayang'],
      Hal: ['Berempati', 'Pencinta keluarga', 'Santai', 'Kreatif'],
    }
  };

  const handleAnswer = (index: number) => {
    const selectedCharacter = questions[currentQuestion].characters[index];
    const newScores = characterScores.map(char => ({
      ...char,
      score: char.name === selectedCharacter ? char.score + 1 : char.score
    }));
    setCharacterScores(newScores);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(q => q + 1);
    } else {
      setShowResult(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setCharacterScores([
      { name: 'Malcolm', score: 0 },
      { name: 'Dewey', score: 0 },
      { name: 'Reese', score: 0 },
      { name: 'Francis', score: 0 },
      { name: 'Hal', score: 0 },
      { name: 'Lois', score: 0 },
    ]);
    setShowResult(false);
  };

  const getMatchCharacter = () => {
    return characterScores.reduce((prev, current) => 
      (current.score > prev.score) ? current : prev
    );
  };

  if (showResult) {
    const matchCharacter = getMatchCharacter();
    const totalQuestions = questions.length;
    
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6 text-center">{t('quiz.result')}</h2>
        
        {/* 主要匹配角色 */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-semibold mb-4">{matchCharacter.name}</h3>
          <p className="text-gray-600 mb-4">
            {t('quiz.matchLevel')}: {((matchCharacter.score / totalQuestions) * 100).toFixed(0)}%
          </p>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            {characterDetails[matchCharacter.name].description[language]}
          </p>
        </div>

        {/* 所有角色匹配度 */}
        <div className="mb-8">
          <h4 className="font-semibold mb-4 text-lg">{t('quiz.allMatches')}:</h4>
          <div className="space-y-4">
            {characterScores.sort((a, b) => b.score - a.score).map(char => (
              <div key={char.name} className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{char.name}</span>
                  <span className="text-sm text-gray-600">
                    {((char.score / totalQuestions) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${(char.score / totalQuestions) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 性格特征 */}
        <div className="mb-8">
          <h4 className="font-semibold mb-4 text-lg">{t('quiz.traits')}:</h4>
          <div className="flex flex-wrap gap-2">
            {characterTraits[language][matchCharacter.name].map((trait, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {trait}
              </span>
            ))}
          </div>
        </div>

        {/* 推荐剧集 */}
        <div className="mb-8">
          <h4 className="font-semibold mb-4 text-lg">{t('quiz.recommendedEpisodes')}:</h4>
          <div className="space-y-4">
            {characterDetails[matchCharacter.name].recommendedEpisodes.map((episode, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium">
                  {t('quiz.seasonEpisode', {
                    season: episode.season,
                    episode: episode.episode
                  })}: {episode.title[language]}
                </h5>
                <p className="text-gray-600 mt-2">{episode.description[language]}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 相似角色 */}
        <div className="mb-8">
          <h4 className="font-semibold mb-4 text-lg">{t('quiz.similarCharacters')}:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {characterDetails[matchCharacter.name].similarCharacters.map((charName) => (
              <div key={charName} className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium mb-2">{charName}</h5>
                <p className="text-gray-600 text-sm">
                  {characterDetails[charName].description[language]}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 重新测试按钮 */}
        <button
          onClick={resetQuiz}
          className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
          {t('quiz.retake')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">{t('quiz.title')}</h2>
        <div className="text-gray-600">
          {t('quiz.questionNumber', { current: currentQuestion + 1, total: questions.length })}
        </div>
      </div>
      <div className="space-y-6">
        <h3 className="text-xl font-semibold">
          {t(`quiz.questions.${questions[currentQuestion].question}`)}
        </h3>
        <div className="grid gap-4">
          {questions[currentQuestion].options[language].map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className="w-full text-left px-4 py-3 border rounded hover:bg-blue-50 transition-colors"
            >
              {option}
            </button>
          ))}
          </div>
        <p className="text-sm text-gray-500 italic mt-4">
          {t('quiz.disclaimer')}
        </p>
      </div>
    </div>
  );
}; 